import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { LOGO } from "@/data/exercises";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    if (password !== confirm) {
      setError("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Mot de passe réinitialisé. Vous pouvez vous connecter.");
      navigate("/connexion");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-[#F4F0E8] font-care">
      <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-sm p-8" data-testid="reset-card">
        <img src={LOGO} alt="Passerelle" className="h-14 w-auto mb-4" />
        <div className="flex items-center gap-2 text-emerald-700 mb-1">
          <ShieldCheck className="w-6 h-6" />
          <h2 className="font-serif-display text-2xl font-normal text-gray-900">Nouveau mot de passe</h2>
        </div>
        <p className="text-gray-500 mb-6">Choisissez un nouveau mot de passe pour votre compte.</p>

        {!token && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="reset-no-token">
            Lien invalide. Veuillez refaire une demande de réinitialisation.
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="reset-error">
            {error}
          </div>
        )}

        <form onSubmit={submit} data-testid="reset-form">
          <div className="space-y-4">
            <div>
              <Label htmlFor="password">Nouveau mot de passe</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1 h-12"
                data-testid="reset-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirm">Confirmer le mot de passe</Label>
              <Input
                id="confirm"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="••••••••"
                className="mt-1 h-12"
                data-testid="reset-confirm-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading || !token}
            className="w-full h-12 mt-6 text-base bg-emerald-700 hover:bg-emerald-800 rounded-xl"
            data-testid="reset-submit-button"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Réinitialiser"}
          </Button>
        </form>

        <Link
          to="/connexion"
          className="mt-6 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-emerald-700"
          data-testid="reset-back-login"
        >
          <ArrowLeft className="w-4 h-4" /> Retour à la connexion
        </Link>
      </div>
    </div>
  );
}
